import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Button, Icon, Loader } from "@src/components/atoms";
import AppUtils from "@src/utils/appUtils";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
} from "@tanstack/react-table";

import { formatMass } from "@signolteam/shared/units-of-measure";
import { useWindowDimensions } from "@src/utils/mediaQuery";
import { COMMON_CONTROLS } from "@src/stories/utils/common-controls";
import { SignolUtils } from "@src/utils";
import { CUSTOMER_LOGO_URL, NO_IMAGE_FOUND } from "@src/constants/constants";
import { useNavigate } from "react-router-dom";
import { useAdminContext } from "@src/context/adminContext";

const getColumns = (customers, industry, width, onEditCustomer) => {
  const isShipping = industry === "shipping";
  const isMobile = width < 768; //under 768px
  const isMobileLG = width < 1024; //under 1024px

  let aviationColumns = [
    {
      header: (
        <div className="text-left font-bold hover:text-primary-500 cursor-pointer pl-2">
          Company Logo
        </div>
      ),
      id: "departure",
      accessorFn: (customer) => customer.customerCode,
      cell: (props) => (
        <div className="px-2">
          <img
            className="h-8 w-auto mx-auto"
            src={CUSTOMER_LOGO_URL(props.getValue())}
            alt={props.getValue()}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = NO_IMAGE_FOUND;
            }}
          />
        </div>
      ),
      enableSorting: true,
    },
    {
      header: (
        <div
          className={`font-bold hover:text-primary-500 cursor-pointer text-left `}
        >
          Code
        </div>
      ),
      id: "code",
      accessorFn: (c) => c.customerCode,
      cell: (props) => <div className="text-left">{props.getValue()}</div>,
    },
    {
      header: (
        <div
          className={`font-bold hover:text-primary-500 cursor-pointer text-left `}
        >
          Name
        </div>
      ),
      id: "name",
      accessorFn: (c) => c.name,
      cell: (props) => <div>{props.getValue()}</div>,
    },
    {
      header: (
        <div
          className={`font-bold hover:text-primary-500 cursor-pointer text-left px-4`}
        >
          Industry
        </div>
      ),
      id: "industry",
      accessorFn: (c) => c.industry,
      cell: (props) => (
        <div>
          <Icon
            name={SignolUtils.getIndustryIcon(props.getValue())}
            backgroundColor="transparent"
            size="xl"
          />
        </div>
      ),
    },
    {
      header: (
        <div
          className={`font-bold hover:text-primary-500 cursor-pointer text-left px-4 `}
        >
          Language
        </div>
      ),
      id: "language",
      accessorFn: (c) => c?.language?.toUpperCase(),
      cell: (props) => <div className="text-center">{props.getValue()}</div>,
    },
    {
      header: (
        <div className="font-bold hover:text-primary-500 cursor-pointer text-center">
          Active
        </div>
      ),
      id: "isActive",
      accessorFn: (c) => c.isActive,
      cell: (props) => (
        <div className="text-center">
          {props.getValue() ? (
            <span className="text-green-600 font-semibold"> ✅ Yes</span>
          ) : (
            <span className="text-red-500 font-semibold">❌ No</span>
          )}
        </div>
      ),
      enableSorting: true,
    },
    {
      header: (
        <div
          className={`font-bold hover:text-primary-500 cursor-pointer text-center`}
        >
          Actions
        </div>
      ),
      id: "actions",
      accessorFn: (c) => c,
      cell: (props) => (
        <div className="w-5/12 text-center flex text-xs">
          <Button
            color="neutral"
            onClick={() => {
              onEditCustomer(props.getValue(), "main");
            }}
          >
            Edit
          </Button>
          <Button
            color="primary"
            onClick={() => {
              onEditCustomer(props.getValue(), "behaviours");
            }}
          >
            Behaviours
          </Button>
          <Button
            color="primary"
            onClick={() => {
              onEditCustomer(props.getValue(), "users");
            }}
          >
            Users
          </Button>
        </div>
      ),
    },
  ];

  return aviationColumns;
};

const CustomersTable = (props) => {
  const {
    customers = [],
    events = [],
    isLoading,
    onCustomerClick,
    className,
    industry = "shipping",
  } = props;

  const { width } = useWindowDimensions();
  const [data, _setData] = useState(events);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [sorting, setSorting] = useState([
    { id: "isActive", desc: true }, // active customers first
    { id: "code", desc: false }, // alphabetical order
  ]);
  const navigate = useNavigate();
  const admin = useAdminContext();
  const { setSelectedCustomer } = admin;

  const onEditCustomer = (data, tab) => {
    navigate(`/dashboard/admin/customers/${data.id}`, {
      state: { tab: tab ? tab : "main" },
    });
  };

  const table = useReactTable({
    data,
    columns: getColumns(customers, industry, width, onEditCustomer),
    state: {
      columnVisibility,
      sorting,
    },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
  });

  useEffect(
    (prev) => {
      setColumnVisibility({
        ...prev,
        CO2: width > 768,
      });
    },
    [width],
  );

  useEffect(() => {
    _setData(events);
  }, [events]);

  return (
    <div className={className}>
      {isLoading && <Loader size="2xl" />}

      {!isLoading && !!table && data.length > 0 && (
        <div className="p-2">
          <table className="w-full divide-y divide-gray-200 ">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="border-b border-gray-500">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      className={
                        header.column.getCanSort()
                          ? "cursor-pointer select-none"
                          : ""
                      }
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className={`border-b border-gray-200`}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              {table.getFooterGroups().map((footerGroup) => (
                <tr key={footerGroup.id}>
                  {footerGroup.headers.map((header) => (
                    <th key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.footer,
                            header.getContext(),
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </tfoot>
          </table>
          <div className="h-4" />
        </div>
      )}
    </div>
  );
};

export const customersTableControls = {
  customers: {
    propType: PropTypes.array,
    description: "Array of customers",
    controlType: "object",
    defaultValue: [],
  },
  isLoading: COMMON_CONTROLS.isLoading,
  className: COMMON_CONTROLS.className,
  industry: COMMON_CONTROLS.industry,
};

CustomersTable.propTypes = AppUtils.getPropTypeFromControls(
  customersTableControls,
);

export default CustomersTable;
